"use client";

// TODO: Reevaluate after @highcharts/react is updated.
/**
 * Problem: this.pane.hasSeriesType('gauge') — unguarded in `wrapAxisGetMinorTickInterval``
 * Fix: this.pane?.hasSeriesType('gauge') — optional chaining to prevent errors
 * Solution: Temporary enforce deterministic load order to ensure evaluation order of Highcharts modules
 * Note: Core master must be evaluated first: `highcharts-more` patches `G.Axis`/`G.Chart` prototypes.
 * Note: This doesn't impact bundle since home already ships it.
 */
import "highcharts/es-modules/masters/highcharts.src.js";
import "highcharts/es-modules/masters/highcharts-more.src.js";
import { useIsRestoring } from "@tanstack/react-query";
import { redirect, usePathname } from "next/navigation";
import { ErrorBoundary } from "react-error-boundary";

import {
  useRequiredData,
  useRequiredQueriesPreloader,
} from "#app/(required)/useRequiredData";
import { Segment } from "#app/(unauthorized)/authentication/segment";
import {
  useJwtRequiredActivities,
  useJwtSegment,
} from "#app/(unauthorized)/authentication/useJwt";
import { ErrorFallback } from "#app/_ui/components/errors/error-fallback";
import { AuthorizedFooter } from "#app/_ui/components/footers/AuthorizedFooter";
import { AutoLogoff } from "#app/_ui/components/headers/autoLogoff/AutoLogoff";
import { Header } from "#app/_ui/components/headers/header";
import { MaintenanceCard } from "#app/_ui/components/maintenance/MaintenanceCard";
import { MaintenanceLayout } from "#app/_ui/components/maintenance/MaintenanceLayout";
import { PageOutageAlertBanner } from "#app/_ui/components/outages/page-outage-alert-banner";
import { useQueryPageOutage } from "#app/_ui/components/outages/use-query-page-outage";
import AppProgressPending from "#app/_ui/components/pages/AppProgressPending";
import { logTrace } from "#app/lib/logger";
import { buildRedirectUrl } from "#app/lib/searchParamsUtil";
import { LandmarkLink } from "#ui/landmark-link";

export default function SecuredLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const isRestoring = useIsRestoring();

  const requiredActivities = useJwtRequiredActivities();
  const hasRequiredActivities = requiredActivities.length > 0;

  const { isPendingRequiredData } = useRequiredData();
  useRequiredQueriesPreloader();

  const pathname = usePathname();
  const {
    data: dataPageOutage,
    failureCount: failureCountPageOutage,
    isPending: isPendingPageOutage,
  } = useQueryPageOutage({ path: pathname });
  const isPendingPageOutageData =
    isPendingPageOutage && failureCountPageOutage === 0;

  const segment = useJwtSegment();
  if (segment == Segment.NotLoggedIn) {
    logTrace("Unauthenticated, redirecting...");
    return redirect(buildRedirectUrl());
  }

  if (
    isRestoring ||
    hasRequiredActivities ||
    isPendingRequiredData ||
    isPendingPageOutageData
  ) {
    return <AppProgressPending />;
  }

  return (
    <>
      <AutoLogoff />
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <LandmarkLink type="trigger" slug="main" label="main content" />
        <LandmarkLink type="trigger" slug="footer" label="footer content" />
        {pathname.startsWith("/sharing") ? null : <Header />}
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <PageOutageAlertBanner data={dataPageOutage} />
          <LandmarkLink type="target" slug="main" label="main content" />
          {!!dataPageOutage && dataPageOutage.isAvailable === false ? (
            <MaintenanceLayout title={dataPageOutage.pageTitle}>
              <MaintenanceCard title={dataPageOutage.sectionTitle}>
                {dataPageOutage.statusMessage}
              </MaintenanceCard>
            </MaintenanceLayout>
          ) : (
            children
          )}
        </ErrorBoundary>
        <LandmarkLink type="target" slug="footer" label="footer content" />
        <AuthorizedFooter />
      </ErrorBoundary>
    </>
  );
}
